import { Static, Type } from "@sinclair/typebox";
import { ApiErrorResponse } from "../ApiErrorResponse";
import type { ETokenInstance } from "../EToken";
import type { ReportedMessage } from "../instant-messaging/messagesEmitter";
import { TUVIS_MESSAGE_SOURCES, TuvisMessageSource } from "../incident/source";

export const ReportMetadata = Type.Object({
  orgId: Type.String(),
  sourcePhone: Type.String(),
  source: Type.Union(TUVIS_MESSAGE_SOURCES.map((s) => Type.Literal(s))),
  sourceUid: Type.Optional(Type.String()),
  destinations: Type.Optional(Type.Array(Type.String())),
});
export type ReportMetadata = Static<typeof ReportMetadata> & { etoken?: ETokenInstance };

export const ReadMessagesMetadata = Type.Object({
  orgId: Type.String(),
  destinations: Type.Optional(Type.Array(Type.String())),
  sourcePhone: Type.Optional(Type.Array(Type.String())),
  userId: Type.Optional(Type.String()),
  fromDate: Type.Optional(Type.String()),
  toDate: Type.Optional(Type.String()),
  getAllMessages: Type.Optional(Type.Boolean()),
  type: Type.Optional(Type.Union([Type.Literal("messages"), Type.Literal("calls")])),
  nextToken: Type.Optional(Type.String()),
  isDownload: Type.Optional(Type.Boolean()),
});
export type ReadMessagesMetadata = Static<typeof ReadMessagesMetadata> & { etoken?: ETokenInstance };

export type ReadMessagesResponse = { success: true; messages: ReportedMessage[]; nextToken?: string } | Static<typeof ApiErrorResponse>;

export const GetMessagesPhonesQueryString = Type.Object({
  orgId: Type.String(),
  destinations: Type.Optional(Type.Array(Type.String())),
  userId: Type.Optional(Type.String()),
});
export type GetMessagesPhonesQueryString = Static<typeof GetMessagesPhonesQueryString> & { etoken?: ETokenInstance };

export const GetMessagesPhonesResponse = Type.Union([
  Type.Object({ success: Type.Literal(true), phones: Type.Array(Type.String()) }),
  ApiErrorResponse,
]);
export type GetMessagesPhonesResponse = Static<typeof GetMessagesPhonesResponse>;

export interface SyncronizedMessage {
  messageId: string;
  orgId: string;
  chatId: string;
  timestamp: number;
  lastUpdate: number;
  sourcePhone: string;
  source: TuvisMessageSource;
}

export const ReportMessagesResponse = Type.Union([Type.Object({ success: Type.Literal(true) }), ApiErrorResponse]);
export type ReportMessagesResponse = Static<typeof ReportMessagesResponse>;

export const IngestQueryString = Type.Object({
  orgId: Type.Optional(Type.String()),
});
export type IngestQueryString = Static<typeof IngestQueryString>;

export const ParsedBlocklistRecord = Type.Object({
  phone: Type.String(),
  isInternal: Type.Boolean(),
});
export type ParsedBlocklistRecord = Static<typeof ParsedBlocklistRecord>;

export const GetMessageTimestampsQuery = Type.Object({
  orgId: Type.String(),
  sourcePhone: Type.String(),
});
export type GetMessageTimestampsQuery = Static<typeof GetMessageTimestampsQuery>;

export const GetMessageTimestampsSuccess = Type.Object({
  success: Type.Literal(true),
  chatIdLatest: Type.Record(Type.String(), Type.Number()),
  potentiallyUpdated: Type.Record(Type.String(), Type.Number()),
  earliestPotentialUpdate: Type.Number(),
  maxMessageAge: Type.Optional(Type.Number()),
  maxFileSizeBytes: Type.Optional(Type.Number()),
  phones_blocked_from_sync: Type.Optional(Type.Union([Type.Array(Type.String()), Type.Array(ParsedBlocklistRecord)])),
  phones_whitelist: Type.Optional(Type.Array(Type.String())),
  /** @deprecated Superseded — collection is now unconditional client-side and reporting is gated at the reporter layer. Only sidecars read this and they deploy with the server, so there's no back-compat window; safe to drop. */
  collectGroupParticipants: Type.Optional(Type.Boolean()),
  syncStatusMessages: Type.Optional(Type.Boolean()),
  syncNewsLetterMessages: Type.Optional(Type.Boolean()),
});
export const GetMessageTimestampsResponse = Type.Union([GetMessageTimestampsSuccess, ApiErrorResponse]);
export type GetMessageTimestampsResponse = Static<typeof GetMessageTimestampsResponse>;

export const GetSyncedMessagesQuery = Type.Object({
  orgId: Type.String(),
  sourcePhone: Type.Optional(Type.String()),
  chatId: Type.String(),
});
export type GetSyncedMessagesQuery = Static<typeof GetSyncedMessagesQuery>;

export const GetSyncedMessagesSuccess = Type.Object({
  success: Type.Literal(true),
  msgIds: Type.Array(Type.String()),
  msgIdLatestUpdate: Type.Record(Type.String(), Type.Number()),
});
export const GetSyncedMessagesResponse = Type.Union([GetSyncedMessagesSuccess, ApiErrorResponse]);
export type GetSyncedMessagesResponse = Static<typeof GetSyncedMessagesResponse>;
